import * as React from 'react';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSnackbar} from 'notistack';

import {
  BASKET_ID_ADDED,
  BASKET_CONTAINS_ID,
  BASKET_ID_REMOVED,
  BASKET_NOT_CONTAINS_ID,
  BASKET_CONTAINS_IDS,
  BASKET_IDS_ADDED,
} from '../constants';
import {BasketContextValue, SearchAssetInformationDto} from '../models';
import {getValue, setValue} from '../utils';
import {useAuth} from './auth';
import {getValidBasket} from '../api';

const noop = () => {};

const BasketContext = React.createContext<BasketContextValue>({
  basket: [],
  setBasket: noop,
});

export const useBasket = () => useContext(BasketContext);

/**
 * Separater Kontext für den Warenkorb-Zustand.
 * Verhindert unnötige Re-Renders von Komponenten, die nur Auth-Daten benötigen.
 */
export const BasketProvider: React.FC<PropsWithChildren> = ({children}) => {
  const {token} = useAuth();
  const {enqueueSnackbar} = useSnackbar();
  const [basket, setBasket] = useState<SearchAssetInformationDto[]>([]);

  // Warenkorb aus LocalStorage laden und validieren bei Token-Änderung
  useEffect(() => {
    if (token) {
      const storedBasket: SearchAssetInformationDto[] = JSON.parse(
        getValue('basket', null) ?? '[]',
      );
      getValidBasket(token, storedBasket, setBasket);
    } else {
      // Wenn kein Token vorhanden ist, lokalen Warenkorb leeren
      setBasket([]);
    }
  }, [token]);

  // Warenkorb im LocalStorage persistieren
  useEffect(() => {
    if (token) {
      setValue('basket', JSON.stringify(basket));
    }
  }, [basket, token]);

  const addToBasket = useCallback(
    (products: SearchAssetInformationDto[]) => {
      const newBasket = [...basket];
      const multipleItems = products.length > 1;
      let counter = 0;
      for (const product of products) {
        if (!newBasket.includes(product)) {
          newBasket.push(product);
          counter++;
          if (!multipleItems) {
            enqueueSnackbar(BASKET_ID_ADDED, {variant: 'success'});
          }
        } else if (!multipleItems) {
          enqueueSnackbar(BASKET_CONTAINS_ID, {variant: 'warning'});
        }
      }

      if (multipleItems) {
        if (counter > 0) {
          enqueueSnackbar(BASKET_IDS_ADDED, {variant: 'success'});
        } else {
          enqueueSnackbar(BASKET_CONTAINS_IDS, {variant: 'warning'});
        }
      }
      setBasket(newBasket);
    },
    [basket, enqueueSnackbar],
  );

  const removeFromBasket = useCallback(
    (product: SearchAssetInformationDto) => {
      const newBasket = [...basket];
      const index = basket.lastIndexOf(product);
      if (index !== -1) {
        newBasket.splice(index, 1);
        enqueueSnackbar(BASKET_ID_REMOVED, {variant: 'success'});
      } else {
        enqueueSnackbar(BASKET_NOT_CONTAINS_ID, {variant: 'error'});
      }

      setBasket(newBasket);
    },
    [basket, enqueueSnackbar],
  );

  const value = useMemo(
    () => ({
      basket,
      setBasket,
      addToBasket,
      removeFromBasket,
    }),
    [basket, setBasket, addToBasket, removeFromBasket],
  );

  return (
    <BasketContext.Provider value={value}>{children}</BasketContext.Provider>
  );
};
